import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/VoiceChatPopup.module.css";
import {
  X,
  FileText,
  Waves,
  ArrowUp,
  Copy,
  Plus,
  AudioLines,
  RotateCcw,
  Files,
} from "lucide-react";
import SessionActions from "./SessionActions";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch, useSelector } from "react-redux";
import { refreshSessionData } from "../../_store/reducers/refreshDataReducer";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import {
  finalizeTraining,
  getTrainingSummary,
  startChat,
  startTraining,
  transcribeAudio,
} from "../../requests";
import SpeechToText from "../common/SpeechToText";
import ResponseLoader from "../common/ResponseLoader";
import { isHTML, formatHTMLResponse, getLanguageCode } from "../helper/formatters";

const AI_AVATAR = `${process.env.PUBLIC_URL}/img/icon-ai-l1.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const VoiceChatPopup = ({ show, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [showSaveSession, setShowSaveSession] = useState(false);
  const [summary, setSummary] = useState(false);
  const [transcript, setTranscript] = useState(false);
  const [generatingTranscript, setGeneratingTranscript] = useState(false);
  const [audioData, setAudioData] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micDisabled, setMicDisabled] = useState(false);
  const [isAIResponseComplete, setIsAIResponseComplete] = useState(true);
  const [isQueryLocked, setIsQueryLocked] = useState(false);
  const audioPlayer = useRef(null); // Ref for the audio element
  const chatEndRef = useRef(null);

  const { reviewSessionTitle } = useSelector((state) => state.reviewSession);

  const dispatch = useDispatch();

  const sessionId = sessionStorage.getItem("trainerSessionId");
  const trainingLanguage = sessionStorage.getItem("trainingLanguage");

  // Manage mic disabled state
  useEffect(() => {
    const shouldDisable = waiting || isSpeaking || !isAIResponseComplete;

    // Add a small delay before enabling to prevent rapid state changes
    if (!shouldDisable) {
      const timeout = setTimeout(() => {
        setMicDisabled(false);
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      setMicDisabled(true);
    }
  }, [waiting, isSpeaking, isAIResponseComplete]);
  
  // Start Chat on Component Mount only Triggered Once
  useEffect(() => {
    if (show) {
      handleStartChat();
    } else {
      resetStates();
    }
    return () => {
      resetStates();
    };
  }, [show]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    if (audioData) {
      playAudio();
    }
  }, [messages, audioData]);

  const resetStates = () => {
    setMessages([]);
    setWaiting(false);
    setHovered("");
    setShowSaveSession(false);
    setSummary(false);
    setAudioData(null);
    stopAudioPlaying();
    setIsAIResponseComplete(true);
    setIsQueryLocked(false);
    setMicDisabled(false);
    // setTimer("15:00");
  };

  const stopAudioPlaying = async () => {
    if (audioPlayer.current) {
      audioPlayer.current.pause();
      // Optionally, you might want to reset the currentTime to 0
      // audioPlayer.current.currentTime = 0;
      // However, revoking the URL is important for resource management
      if (audioPlayer.current.src) {
        URL.revokeObjectURL(audioPlayer.current.src);
      }
      audioPlayer.current = null; // Clear the ref
    }
  };

  const playAudio = () => {
    let audio = audioData?.audio_data;
    if (!audio) {
      setIsAIResponseComplete(true);
      return;
    }
    // Play audio
    const audioBlob = new Blob(
      [Uint8Array.from(atob(audio), (c) => c.charCodeAt(0))],
      { type: "audio/mpeg" }
    );
    const url = URL.createObjectURL(audioBlob);
    const audioElement = new Audio(url);
    audioPlayer.current = audioElement; // Store the audio element in the ref
    // 🔁 Set speaking true when audio starts
    audioElement.onplay = () => {
      setIsSpeaking(true);
    };

    // 🔚 Set speaking false when audioElement ends
    audioElement.onended = () => {
      setIsSpeaking(false);
      setAudioData(null);
      setIsAIResponseComplete(true); // AI finished speaking, mic can be enabled
      setIsQueryLocked(false); // Reset query lock when AI response is complete
    };

    audioElement.onerror = (e) => {
      console.error("audioElement error:", e);
      setIsSpeaking(false);
      setAudioData(null);
      setIsAIResponseComplete(true); // Mic can be enabled on error too
      setIsQueryLocked(false); // Reset query lock on error too
    };

    // ⏯️ Trigger playback
    audioElement.play().catch((err) => {
      console.error("Playback failed:", err);
      setIsSpeaking(false);
      setAudioData(null);
      setIsAIResponseComplete(true); // Mic can be enabled on playback failure
      setIsQueryLocked(false); // Reset query lock on playback failure
    });
  };

  const handleStartChat = async () => {
    setWaiting(true);
    setIsAIResponseComplete(false); // AI is speaking
    try {
      const response = await startTraining({
        sessionId,
        voice: true,
        language_code: trainingLanguage,
      });
      // Trigger a refresh of the current page to get updated data
      const aiText = response.data.message;
      const audioData = response.data.voice_data;
      setMessages([{ sender: "ai", text: aiText }]);
      setAudioData(audioData);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
      setIsAIResponseComplete(true);
      setIsQueryLocked(false); // Reset query lock on error
    }
    setWaiting(false);
  };

  const handleSend = async (transcript) => {
    if (isSpeaking || waiting) {
      return;
    }
    setIsAIResponseComplete(false); // AI is speaking
    setIsQueryLocked(true); // Lock query when sending

    if (!transcript) {
      notifyError("Please record your message first.");
      return;
    }
    // to regenerate summary
    if (summary) {
      setSummary(false);
    }

    if (!transcript) {
      transcript = "Failed to generate transcript. Please try again.";
      setMessages((msgs) => [...msgs, { sender: "dr", text: transcript }]);
      return;
    }
    setMessages((msgs) => [...msgs, { sender: "dr", text: transcript }]);
    setWaiting(true);
    const aiText = await handleChat(transcript);
    setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    setWaiting(false);
  };


  const handleChat = async (userMessage) => {
    try {
      const response = await startChat({
        sessionId,
        user_msg: userMessage,
        voice: true,
      });
      const aiText = response.data.message;
      const audioData = response.data.voice_data;
      setAudioData(audioData);
      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleOpenSaveSession = () => {
    setShowSaveSession(true);
  };

  const handleFinalizeSession = async (
    isPublish = false,
    isDraft = 0,
    isSave = 0
  ) => {
    dispatch(startLoader());
    try {
      await finalizeTraining({ sessionId, isPublish, isDraft });
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError("Something went wrong. Please try again later.");
      console.error("Error Sending Message:", error);
    }
    dispatch(stopLoader());
    setShowSaveSession(false);
    if (isPublish || isDraft || isSave) {
      onClose();
      dispatch(refreshSessionData());
    }
  };

  const generateSummary = async () => {
    try {
      const response = await getTrainingSummary({ sessionId });
      const aiSummary = response.data.message;

      return aiSummary;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleGenerateSummary = async () => {
    setWaiting(true);
    setShowSaveSession(false);
    const aiSummary = await generateSummary();
    setMessages((msgs) => [
      ...msgs,
      { sender: "ai", text: aiSummary, summary: true },
    ]);
    setWaiting(false);
    setSummary(true);
  };

  const handleTranscriptReady = (transcript) => {
    handleSend(transcript);
  };

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div className={styles.popup}>
            <div
              className={`${styles.mainContainer} ${
                transcript ? styles.withTranscript : ""
              }`}
              style={{
                height: "80vh",
              }}
            >
              <div
                className={`${styles.voiceChatUI} ${
                  transcript ? styles.voiceChatUIShifted : ""
                }`}
              >
                {!transcript && (
                  <button className={styles.closeBtn} onClick={onClose}>
                    <X size={22} />
                  </button>
                )}
                <div
                  style={{
                    width: transcript ? "100%" : "50%",
                  }}
                  className={styles.voiceChatUIContent}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/img/icon-ai-l2.png`}
                    alt="voice chat ui"
                  />
                  <p>Now Training you AI</p>
                  <h4>
                    {reviewSessionTitle}
                  </h4>
                </div>
              </div>
              <div
                className={`${styles.chatBody} ${
                  transcript ? styles.chatBodyVisible : styles.chatBodyHidden
                }`}
              >
                <div className={styles.chatBodyHeader}>
                  <h5 className={styles.title}>Live Transcript</h5>
                  <button
                    className={styles.closeBtnTranscript}
                    onClick={onClose}
                  >
                    <X size={22} />
                  </button>
                </div>
                <hr className="m-0" />
                {transcript && (
                  <div className={styles.chatBodyContent}>
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={
                          msg.sender === "ai" ? styles.aiMsg : styles.drMsg
                        }
                      >
                        <div className={styles.msgText}>
                          <div className={styles.avatar}>
                            <i>
                              <img
                                src={
                                  msg.sender === "ai" ? AI_AVATAR : DR_AVATAR
                                }
                                alt="avatar"
                              />
                            </i>

                            <span>{msg.sender === "ai" ? "AI" : "Salah"}</span>
                          </div>
                          {msg?.summary && <strong>Summary Generated:</strong>}
                          {isHTML(msg.text) ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatHTMLResponse(msg.text),
                              }}
                            />
                          ) : (
                            <p className="m-0 mt-2">{msg.text}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {waiting && (
                      <div className={styles.aiMsg}>
                        <div className={styles.msgText}>
                          <div className={styles.avatar}>
                            <i>
                              <img src={AI_AVATAR} alt="avatar" />
                            </i>

                            <span>"AI"</span>
                          </div>

                          <ResponseLoader />
                        </div>
                      </div>
                    )}
                    {generatingTranscript && (
                      <div className={styles.drMsg}>
                        <div className={styles.msgText}>
                          <div className={styles.avatar}>
                            <i>
                              <img src={DR_AVATAR} alt="avatar" />
                            </i>

                            <span>"Salah"</span>
                          </div>

                          <ResponseLoader />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </div>
            </div>
            <hr className="m-0" />
            <div className={styles.inputRow}>
              <div className={styles.bottomBar}>
                <div
                  onMouseEnter={() => setHovered("transcript")}
                  onMouseLeave={() => setHovered("")}
                  className={
                    styles.bottomIconWrap +
                    (transcript ? " " + styles.active : "")
                  }
                  onClick={() => setTranscript(!transcript)}
                >
                  <FileText className={styles.bottomIcon} />
                  <span
                    className={
                      hovered === "transcript"
                        ? styles.bottomTextShow
                        : styles.bottomTextHide
                    }
                  >
                    Transcript
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <SpeechToText
                    handleQuery={handleTranscriptReady}
                    isQueryLockedProp={isQueryLocked}
                    isDisabled={micDisabled}
                    language={getLanguageCode(trainingLanguage)}
                    aiAudioCompleted={isAIResponseComplete}
                    styles={styles}
                  />
                  {/* <button
                    onClick={recordingStart}
                    className="record-icon box-shadow"
                  >
                    <Mic strokeWidth={1} size={20} color="#0E46A3" />
                  </button>
                  ) : (
                  <button onClick={recordingStop}>
                    <StopCircle size={28} color="#0e46a3" />
                  </button> */}
                </div>

                {!summary && (
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("summary")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handleGenerateSummary}
                  >
                    <Files className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "summary"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      Generate Summary
                    </span>
                  </div>
                )}
                {summary && (
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("summary")}
                    onMouseLeave={() => setHovered("")}
                  >
                    <RotateCcw className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "summary"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      Regenerate Summary
                    </span>
                  </div>
                )}

                <div
                  className={styles.bottomIconWrap}
                  onMouseEnter={() => setHovered("save")}
                  onMouseLeave={() => setHovered("")}
                  onClick={handleOpenSaveSession}
                >
                  <AudioLines className={styles.bottomIcon} />
                  <span
                    className={
                      hovered === "save"
                        ? styles.bottomTextShow
                        : styles.bottomTextHide
                    }
                  >
                    Save Session
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSaveSession &&
        createPortal(
          <SessionActions
            show={showSaveSession}
            onHide={() => setShowSaveSession(false)}
            generateSummary={handleGenerateSummary}
            saveDraft={() => handleFinalizeSession(0, 1, 0)}
            publishSession={() => handleFinalizeSession(1, 0, 0)}
            saveSession={() => handleFinalizeSession(0, 0, 1)}
            summary={summary}
          />,
          document.body
        )}
    </>
  );
};

export default VoiceChatPopup;
